import React, {
  ReactElement,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import styled from 'styled-components';
import usePlacesAutocomplete, {
  GeocodeResult,
  getGeocode,
} from 'use-places-autocomplete';
import { Input } from '../Input';
import {
  AddressAutocompleteProps,
  AutocompleteValue,
} from './AddressAutocomplete.types';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { processAddressComponents } from '../../util/addressAutocomplete.util';
import classnames from 'classnames';
import { OptionList } from '../OptionList';

import { isAddressPoBox } from '../../util/validator.util';

const ARROW_UP = 38;
const ARROW_DOWN = 40;
const ENTER = 13;

// styled-component
const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const AddressAutocomplete = (props: AddressAutocompleteProps) => {
  const { onSelect, inputProps, onChange, disableAutocomplete } = props;
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    defaultValue: inputProps.value || '',
    requestOptions: {
      types: ['address'],
    },
    debounce: 300,
  });
  const [canSeeOptions, setCanSeeOptions] = useState(false);
  //Added cursor to track current position of the button
  const [cursor, setCursor] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  const handleClickOutside = useCallback(() => {
    setCanSeeOptions(false);
    clearSuggestions();
  }, [setCanSeeOptions, clearSuggestions]);

  useEffect(() => {
    if (inputProps?.value) setValue(inputProps?.value);
  }, [inputProps.value, setValue]);

  useOnClickOutside(ref, handleClickOutside);

  const handleInput: React.ChangeEventHandler<HTMLInputElement> = e => {
    setValue(e.target.value);
    if (onChange) onChange(e.target.value);
    // only show the options if the autocomplete is not disabled
    setCanSeeOptions(!disableAutocomplete);
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    // If the last element is highlighted, then the down arrow wraps the cursor back around to the first element
    // Else, the down arrow advances the cursor ahead by 1
    if (event.keyCode === ARROW_DOWN) {
      setCursor(pointer => (pointer === data.length ? 1 : pointer + 1));
    }

    // If the last element is highlighted, then the down arrow wraps the cursor back around to the first element
    // Else, the up arrow advances the cursor backward by 1
    if (event.keyCode === ARROW_UP) {
      setCursor(pointer => (pointer === 1 ? data.length : pointer - 1));
    }

    // If the button pressed is Enter/Return then it goes ahead to select the suggestions
    if (event.keyCode === ENTER) {
      event.preventDefault();
      const suggestion = data[cursor - 1];
      if (suggestion) {
        handleSelect(suggestion)();
      }
    }
  };

  const handleSelect = suggestion => async () => {
    let address: AutocompleteValue | null = null;
    let geoResults: GeocodeResult[] = [];
    try {
      geoResults = await getGeocode({
        address: suggestion.description,
      });
      address = processAddressComponents(geoResults[0].address_components);
    } catch (error) {
      console.error(
        'Error when attempting to obtain address suggestions.',
        error,
      );
    }

    if (address) {
      onSelect(address, geoResults[0]);
      // The value to put in the autocomplete input after they select an option
      setValue(address.streetAddress, false);
    }

    clearSuggestions();
    setCanSeeOptions(false);
    setCursor(0);
  };

  const renderSuggestions = (): ReactElement[] => {
    return (
      data
        .filter(suggestion => suggestion.matched_substrings?.[0]?.offset === 0)
        // autocomplete doesn't suggest APO addresses or PO boxes in format #XXXXXX
        // so just filter for any stray PO boxes included ('P.O.Box No: TN 2051,Teshie-Nungua East, Spintex Road')
        .filter(suggestion => !isAddressPoBox(suggestion.description))
        .map((suggestion, index) => {
          const {
            place_id,
            structured_formatting: { main_text, secondary_text },
          } = suggestion;
          return (
            <li
              key={place_id}
              onClick={handleSelect(suggestion)}
              className={classnames('', {
                active: cursor - 1 === index,
              })}
            >
              <strong>{main_text}</strong> <small>{secondary_text}</small>
            </li>
          );
        })
    );
  };

  return (
    <InputContainer ref={ref}>
      <Input
        value={value}
        onChange={handleInput}
        disabled={!ready}
        // So that browsers don't show an autofill dialog on top of the address select dropdown
        autoComplete="off"
        name={inputProps.name}
        className={inputProps.className}
        id={inputProps.id}
        data-testid={inputProps.id}
        label={inputProps.label}
        icon={inputProps.icon}
        tooltip={inputProps.tooltip}
        onFocus={inputProps.onFocus}
        onBlur={inputProps.onBlur}
        active={inputProps.active}
        onKeyDown={handleKeyDown}
      />
      {status === 'OK' && canSeeOptions && (
        <OptionList data-label={inputProps.label}>
          {renderSuggestions()}
        </OptionList>
      )}
    </InputContainer>
  );
};

export default AddressAutocomplete;
